import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Envelope, Lock, ShieldCheck, User } from "@phosphor-icons/react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export default function Register() {
    const { register, login } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Bot defenses
    const [website, setWebsite] = useState("");          // honeypot
    const [turnstileToken, setTurnstileToken] = useState("");
    const turnstileRef = useRef(null);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        // When Turnstile is configured we require a token before submit.
        if (SITE_KEY && !turnstileToken) {
            toast.error("Please complete the bot check before continuing");
            try { turnstileRef.current?.execute(); } catch { /* noop */ }
            return;
        }
        setSubmitting(true);
        try {
            const res = await register(name, email, password, {
                turnstileToken,
                website,
            });
            if (res?.autoLogin) {
                await login(email, password);
                toast.success("Welcome to KickLoom");
                navigate("/dashboard");
            } else {
                toast.success("Account created! Check your email to verify.");
                navigate("/login");
            }
        } catch (err) {
            toast.error(err?.message || "Registration failed");
            // Reset the widget so the user can retry.
            try { turnstileRef.current?.reset(); } catch { /* noop */ }
            setTurnstileToken("");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout withFooter={false}>
            <Seo title="Register" path="/register" />
            <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-3 text-center">
                        ▌ JOIN
                    </div>
                    <h1 className="text-4xl font-black uppercase tracking-tighter text-center mb-10">
                        Create Account
                    </h1>
                    <form
                        onSubmit={onSubmit}
                        className="space-y-5 bg-card border border-border p-8"
                        data-testid="register-form"
                    >
                        {/* Honeypot — hidden from humans via CSS, bots fill every field. */}
                        <div
                            aria-hidden="true"
                            tabIndex={-1}
                            style={{
                                position: "absolute",
                                left: "-10000px",
                                width: "1px",
                                height: "1px",
                                overflow: "hidden",
                            }}
                        >
                            <label htmlFor="website">Website (leave blank)</label>
                            <input
                                id="website"
                                name="website"
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="name" className="font-mono text-xs uppercase tracking-widest text-zinc-400">
                                Full Name
                            </Label>
                            <div className="relative">
                                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                                <Input
                                    id="name"
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Jane Bettor"
                                    className="pl-10 rounded-none bg-background border-border focus:border-primary focus:ring-1 focus:ring-primary"
                                    data-testid="register-name-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest text-zinc-400">
                                Email
                            </Label>
                            <div className="relative">
                                <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="pl-10 rounded-none bg-background border-border focus:border-primary focus:ring-1 focus:ring-primary"
                                    data-testid="register-email-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="font-mono text-xs uppercase tracking-widest text-zinc-400">
                                Password (min 8 chars)
                            </Label>
                            <div className="relative">
                                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                                <Input
                                    id="password"
                                    type="password"
                                    required
                                    minLength={8}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="pl-10 rounded-none bg-background border-border focus:border-primary focus:ring-1 focus:ring-primary"
                                    data-testid="register-password-input"
                                />
                            </div>
                        </div>

                        {SITE_KEY ? (
                            <div data-testid="turnstile-widget">
                                <Turnstile
                                    ref={turnstileRef}
                                    siteKey={SITE_KEY}
                                    onSuccess={setTurnstileToken}
                                    onError={() => setTurnstileToken("")}
                                    onExpire={() => setTurnstileToken("")}
                                    options={{
                                        action: "register",
                                        theme: "dark",
                                        size: "flexible",
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="flex items-start gap-2 text-[11px] font-mono uppercase tracking-widest text-zinc-500 border border-dashed border-border p-3">
                                <ShieldCheck size={14} className="text-primary mt-0.5" />
                                <span>
                                    Bot protection active · honeypot + rate-limit
                                    {" "}(Cloudflare Turnstile widget loads when{" "}
                                    <span className="text-primary">REACT_APP_TURNSTILE_SITE_KEY</span>{" "}
                                    is configured).
                                </span>
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider h-11"
                            data-testid="register-submit-btn"
                        >
                            {submitting ? "Creating account..." : "Create Account"}
                        </Button>
                        <div className="text-center text-xs font-mono uppercase tracking-widest text-zinc-400 pt-2">
                            Already have an account?{" "}
                            <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                                Login →
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
}
